import React, { useEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { Modal } from 'flowbite';
import moment from 'moment';

import PurchaseOrdersViewModal from '../purchase_orders/PurchaseOrdersViewModal';

import { formatFigure, formatMoney } from 'Helpers/formatService';
import { modalOptions } from 'Helpers/modalOptions';
import { apiClient } from 'Helpers/network';

import Tags from 'CommonComponents/Tags';
import TextButton from 'CommonComponents/TextButton';
import Tooltip from 'CommonComponents/Tooltip';

import { useConfirmationModal } from 'Hooks/useConfirmationModal';
import { useToast } from 'Hooks/useToast';

import CircleCheckIcon from 'Assets/icons/circle-check.svg';
import CirclePlusIcon from 'Assets/icons/circle-plus.svg';
import CircleXMarkIcon from 'Assets/icons/circle-xmark.svg';
import EditIcon from 'Assets/icons/edit.svg';
import EyeIcon from 'Assets/icons/eye.svg';
import TrashIcon from 'Assets/icons/trash.svg';

const figure = (value) => formatFigure(value, formatMoney, { precision: 3 });

export default function SupplierContractsIndexTableRow(props) {
    const [showedExpenditureApprovals, setShowedExpenditureApprovals] = useState(false);
    const [viewedPO, setViewedPO] = useState({});
    const [canceledPO, setCanceledPO] = useState({});

    const [purchaseOrdersViewModalElement, setPurchaseOrdersViewModalElement] = useState(null);

    const purchaseOrdersViewModalRef = useRef();

    const term = useMemo(() => props.supplierContract.term, [props.supplierContract.term]);

    const total = useMemo(() => {
        if (!props.forDeletedContracts) {
            return props.supplierContract.cashflow?.total;
        }
        return props.supplierContract.purchase_orders?.reduce((acc, purchase_order) => acc + purchase_order.final_price, 0) || 0;
    }, [props.supplierContract, props.forDeletedContracts]);

    const toast = useToast();

    const confirm = useConfirmationModal();

    useEffect(() => {
        if (showedExpenditureApprovals) {
            purchaseOrdersViewModalRef && setPurchaseOrdersViewModalElement(new Modal(purchaseOrdersViewModalRef.current, modalOptions(false)));
        }
    }, [showedExpenditureApprovals]);

    useEffect(() => {
        if (Object.keys(viewedPO).length > 0) {
            purchaseOrdersViewModalElement && purchaseOrdersViewModalElement.show();
        }
    }, [viewedPO]);

    useEffect(() => {
        if (Object.keys(canceledPO).length > 0) {
            confirm({
                title: $t('Cancellation of Expenditure Approvals'),
                message: cancelPoConfirmationModalMsg(canceledPO),
                warning: $t(
                    'Deleting a committed expense will also delete the budget held for this commitment. You will need to raise the budget expense line again if you still require these items',
                ),
                onConfirm: () => triggerCancelPO(),
                onClose: () => closeModal(),
            });
        }
    }, [canceledPO]);

    const viewPO = (po) => {
        setViewedPO({ ...po });
    };

    const cancelPO = (po) => {
        setCanceledPO({ ...po });
    };

    const successHandler = () => {
        setViewedPO({});
        setCanceledPO({});
        props.fetchSupplierContracts();
        purchaseOrdersViewModalElement && purchaseOrdersViewModalElement.hide();
    };

    const closeModal = () => {
        setViewedPO({});
        setCanceledPO({});
        purchaseOrdersViewModalElement && purchaseOrdersViewModalElement.hide();
    };

    const triggerCancelPO = () => {
        axios
            .put(props.cancelPOUrl.replace('ORDER_ID', canceledPO.id))
            .then((response) => {
                successHandler();
            })
            .catch(() => toast.error($t('Expenditure approval could not be canceled')))
            .finally(() => toast.success($t('Expenditure approval canceled successfully')));
    };

    const cancelPoConfirmationModalMsg = (selectedCanceledPO) => {
        if (selectedCanceledPO?.purchase_requisition) {
            return $t('Cancel Expenditure Approvals of Purchase Requisition') + ' [' + selectedCanceledPO?.purchase_requisition.pr_code + ']';
        }
        if (!selectedCanceledPO?.purchase_requisition) {
            return $t('Cancel the Expenditure Approvals');
        }
        return '';
    };

    const showExpenditureApprovals = () => {
        setShowedExpenditureApprovals(!showedExpenditureApprovals);
    };

    const updateSignedContract = (signed) => {
        apiClient
            .put(props.updateSignedContractUrl.replace('CONTRACT_ID', props.supplierContract.id), { signed })
            .then(({ data }) => {
                toast.success(data.message);
                props.fetchSupplierContracts();
            })
            .catch(() => toast.error($t('Contract signed status could not be updated')));
    };

    const handleSignedColumnDoubleClick = (e, supplierContract) => {
        e.preventDefault();
        e.stopPropagation();
        updateSignedContract(!supplierContract.contract_signed);
    };

    return (
        <>
            <tr key={props.supplierContract.id}>
                {/* Hidden checkbox cell */}
                <td className={classNames({ 'tw-hidden': !props.selecting })}>
                    <input
                        type="checkbox"
                        className="input-checkbox"
                        checked={props.selectedIds.includes(props.supplierContract.id)}
                        onChange={(e) => props.onSelect(props.supplierContract.id, e.target.checked)}
                    />
                </td>
                <td className="tw-cursor-pointer" onClick={showExpenditureApprovals}>
                    <Tooltip tooltipContent={term?.supply_scope} tooltipType="text" offset={[16, 16]} allowTooltip={term && term.supply_scope}>
                        <div className="tw-cursor-pointer">{props.supplierContract?.contract_reference}</div>
                    </Tooltip>
                </td>
                <td onDoubleClick={(e) => handleSignedColumnDoubleClick(e, props.supplierContract)} className="tw-cursor-pointer tw-select-none">
                    <Tooltip
                        tooltipContent={props.supplierContract.contract_signed ? $t('Double click to mark as unsigned') : $t('Double click to mark as signed')}
                        tooltipType="text"
                    >
                        <div className="tw-h-4 tw-mx-auto tw-flex tw-items-center tw-justify-center">
                            {props.supplierContract.contract_signed && <CircleCheckIcon className="tw-w-4 tw-h-4 tw-text-green-500" />}
                        </div>
                    </Tooltip>
                </td>
                <td>{props.supplierContract.supplier.name}</td>
                {!props.forDeletedContracts && (
                    <td>{props.supplierContract.departments_names?.length ? <Tags tags={props.supplierContract.departments_names} /> : '-'}</td>
                )}
                <td className="inbud-numeric-cell">{figure(total)}</td>
                {!props.forDeletedContracts && (
                    <>
                        <td className="inbud-numeric-cell">{figure(props.supplierContract.actual_spend)}</td>
                        <td className={classNames('inbud-numeric-cell', { 'tw-text-red-75': props.supplierContract.variance < 0 })}>
                            {formatFigure(props.supplierContract.variance)}
                        </td>
                        <td>{props.supplierContract.term?.owner_name}</td>
                        <td className="inbud-numeric-cell">{formatFigure(term?.payments_count, (x) => x)}</td>
                        <td className="inbud-numeric-cell">{formatFigure(term?.milestones_count, (x) => x)}</td>
                    </>
                )}

                <td>
                    {props.forDeletedContracts ? (
                        <div className="tw-flex tw-items-center tw-gap-x-3">
                            <TextButton variant="danger" onClick={() => props.onDeleteContract(props.supplierContract)}>
                                <TrashIcon className="tw-w-3 tw-h-3" />
                            </TextButton>
                        </div>
                    ) : (
                        <div className="tw-flex tw-items-center tw-gap-x-3">
                            {term ? (
                                <>
                                    <TextButton variant="primary" onClick={() => props.onViewTerms(props.supplierContract)}>
                                        <EyeIcon className="tw-w-3 tw-h-3" /> {$t('View Terms')}
                                    </TextButton>
                                    <TextButton variant="primary" onClick={() => props.onEditTerms(props.supplierContract)}>
                                        <EditIcon className="tw-w-3 tw-h-3" />
                                        {$t('Edit Terms')}
                                    </TextButton>
                                </>
                            ) : (
                                <TextButton variant="primary" onClick={() => props.onAddTerms(props.supplierContract)}>
                                    <CirclePlusIcon className="tw-w-3 tw-h-3" /> {$t('Add Terms')}
                                </TextButton>
                            )}
                            {props.isSupplierPortalEnabled && !!props.supplierContract.settings_portal_url && (
                                <TextButton variant="primary" onClick={() => (window.location.href = props.supplierContract.settings_portal_url)}>
                                    <EyeIcon className="tw-w-3 tw-h-3" /> {$t('Portal Settings')}
                                </TextButton>
                            )}
                            <TextButton variant="danger" onClick={() => props.onDeleteContract(props.supplierContract)}>
                                <TrashIcon className="tw-w-3 tw-h-3" />
                            </TextButton>
                        </div>
                    )}
                </td>
            </tr>
            {showedExpenditureApprovals && (
                <tr>
                    <td colSpan={props.forDeletedContracts ? 4 : 12}>
                        <h5 className="table__header tw-mb-3">{$t('Expenditure Approvals')}:</h5>
                        <div className="inbud-table_wrapper">
                            <table className="inbud-table">
                                <thead>
                                    <tr>
                                        <th>{$t('Procurement Description')}</th>
                                        <th>{$t('Value')}</th>
                                        <th>{$t('Status')}</th>
                                        <th>{$t('EA Reference')}</th>
                                        <th>{$t('EA Approval Date')}</th>
                                        <th>{$t('PR Reference')}</th>
                                        <th>{$t('PR Approval Date')}</th>
                                        {!props.forDeletedContracts && <th>{$t('Actions')}</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {props.supplierContract?.purchase_orders?.map((purchase_order) => {
                                        return (
                                            <tr key={purchase_order.id}>
                                                <td>{purchase_order.purchase_requisition?.procurement_plan_item_name || '-'}</td>
                                                <td className="inbud-numeric-cell">{formatFigure(purchase_order.final_price, (x) => x, { precision: 2 })}</td>
                                                <td>{purchase_order.status_label}</td>
                                                <td>{purchase_order.code}</td>
                                                <td className="inbud-date-cell">
                                                    {purchase_order.approved_at ? moment(purchase_order.approved_at).format('YYYY-MM-DD') : '-'}
                                                </td>
                                                <td>{purchase_order.purchase_requisition?.pr_code ? purchase_order.purchase_requisition?.pr_code : '-'}</td>
                                                <td className="inbud-date-cell">
                                                    {purchase_order.purchase_requisition?.approved_at
                                                        ? moment(purchase_order.purchase_requisition?.approved_at).format('YYYY-MM-DD')
                                                        : '-'}
                                                </td>
                                                {!props.forDeletedContracts && (
                                                    <td>
                                                        <div className="tw-flex tw-items-center tw-gap-x-3">
                                                            <TextButton variant="primary" onClick={() => viewPO(purchase_order)}>
                                                                <EyeIcon className="tw-w-3 tw-h-3" /> {$t('View')}
                                                            </TextButton>
                                                            {purchase_order.status_label === 'Approved' && (
                                                                <TextButton variant="danger" onClick={() => cancelPO(purchase_order)}>
                                                                    <CircleXMarkIcon className="tw-w-3 tw-h-3" /> {$t('Cancel')}
                                                                </TextButton>
                                                            )}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>

                            <PurchaseOrdersViewModal
                                modalId={'view-po'}
                                modalRef={purchaseOrdersViewModalRef}
                                modalElement={purchaseOrdersViewModalElement}
                                closeHandler={closeModal}
                                successHandler={successHandler}
                                po={viewedPO}
                                displayApprovalStatuses={props.displayApprovalStatuses}
                            />
                        </div>
                    </td>
                </tr>
            )}
        </>
    );
}

const DepartmentsNames = ({ departmentsNames }) => {
    if (!departmentsNames || !departmentsNames.length) {
        return null;
    }

    return (
        <div className="tw-flex tw-overflow-x-auto tw-gap-1.5">
            {departmentsNames.map((name) => (
                <div className="tw-bg-charcoal-15 tw-text-xs tw-text-nowrap tw-font-medium tw-px-1.5 tw-py-1 tw-rounded-lg" key={name}>
                    {name}
                </div>
            ))}
        </div>
    );
};
